import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { CONFIG } from '../../../config';
import { loadAdyenScript } from '../../../lib/loadScript';
import { encryptCard } from '../../../lib/encryptCard';
import { PaymentSource } from '../../../constants';
import { useCountry } from '../../../components/CountrySelector';
import { useCart } from '../../useCart';
import { PLACE_AFFIRM_ORDER_MUTATION } from './placeAffirmOrderMutation.rest';
import { AffirmCheckoutResponse } from './useAffirmCheckout';

const CARD_EXPIRY_REGEXP = /^([0-9]{2})([0-9]{2})$/;

export const usePlaceAffirmOrderMutation = () => {
  const { country } = useCountry();
  const {
    cart: {
      id: cartId,
      attributes: { addressId, giftCode, selectedShippingMethodId, paymentSource, error: cartError }
    }
  } = useCart();
  const hasInvalidCoupon = cartError?.type.name === 'Invalid Coupon';

  const [mutate, { loading, error }] = useMutation(PLACE_AFFIRM_ORDER_MUTATION);

  useEffect(() => {
    loadAdyenScript();
  }, []);

  const handlePlaceOrder = useCallback(
    (response: AffirmCheckoutResponse) => {
      if (!addressId) return Promise.reject('placeAffirmOrderMutation: no address selected');
      if (!cartId) return Promise.reject('placeAffirmOrderMutation: cart id not found');
      if (!selectedShippingMethodId)
        return Promise.reject('placeAffirmOrderMutation: no shipping method selected');
      if (paymentSource !== PaymentSource.Affirm)
        return Promise.reject('placeAffirmOrderMutation: payment source is not Affirm');

      return mutate({
        variables: {
          input: {
            data: {
              type: 'orders',
              attributes: {
                address_id: addressId,
                cart_id: cartId,
                country_code: country.alpha2Code,
                offer_code: !hasInvalidCoupon ? giftCode : null,
                payment_source: paymentSource,
                shipment_method: selectedShippingMethodId,
                payment_summary: {
                  encrypted_card_data: {
                    encrypted_data: encryptCard({
                      holderName: response.cardholder_name,
                      number: response.number,
                      expiryMonth:
                        CONFIG.ENVIRONMENT === 'prod'
                          ? response.expiration.match(CARD_EXPIRY_REGEXP)?.[1] || ''
                          : '10',
                      expiryYear:
                        CONFIG.ENVIRONMENT === 'prod'
                          ? `20${response.expiration.match(CARD_EXPIRY_REGEXP)?.[2]}`
                          : '2020',
                      cvc: CONFIG.ENVIRONMENT === 'prod' ? response.cvv : '737'
                    })
                  },
                  affirm_checkout_token: response.checkout_token,
                  billing_address: {
                    address_line1: response.billing_address.line1,
                    address_line2: response.billing_address.line2,
                    city: response.billing_address.city,
                    state: response.billing_address.state,
                    country_code: country.alpha2Code,
                    postal_code: response.billing_address.zipcode
                  }
                }
              }
            }
          }
        }
      });
    },
    [
      cartId,
      addressId,
      giftCode,
      selectedShippingMethodId,
      paymentSource,
      country,
      hasInvalidCoupon,
      mutate
    ]
  );

  return [handlePlaceOrder, { loading, error }] as const;
};
